import {
  ActionIcon,
  Menu,
  Pagination,
  Select,
  Skeleton,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowsSort,
  IconBell,
  IconCertificate,
  IconChecks,
  IconChevronDown,
  IconChevronRight,
  IconDots,
  IconDownload,
  IconEye,
  IconFileStack,
  IconMessagePlus,
  IconPencil,
  IconRosetteDiscountCheck,
  IconSearch,
  IconSortAscending,
  IconSortDescending,
  IconTrash,
} from '@tabler/icons-react';
import { Fragment, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { CategoryBadge, CertificateStatus, daysTone } from './CertificateParts.js';
import classes from './Compliance.module.css';
import {
  type CertificateSort,
  type CertificateSortKey,
  DUE_FILTERS,
  type DueFilter,
  pageOf,
  STATUS_FILTERS,
  type StatusFilter,
} from './certificate-view.js';
import type { CertificateListItem } from './types.js';

export const PAGE_SIZE = 15;

export interface CertificateListCardProps {
  scope: string | null;
  /** `null`: yükleniyor. */
  items: readonly CertificateListItem[] | null;
  /** Ağaç düğümündeki (süzgeçsiz) sertifika sayısı: boş kapsam ile eşleşme yok ayrımı. */
  scopeCount: number;
  /** Bütün geminin sertifika sayısı (boş gemi). */
  totalCount: number;
  summary: { valid: number; expiring: number; overdue: number; total: number } | null;
  expiringDays: number;
  typeOptions: { value: string; label: string }[];
  typeId: string | null;
  status: StatusFilter;
  due: DueFilter;
  query: string;
  onTypeId: (value: string | null) => void;
  onStatus: (value: StatusFilter) => void;
  onDue: (value: DueFilter) => void;
  onQuery: (value: string) => void;
  sort: CertificateSort;
  onSort: (key: CertificateSortKey) => void;
  page: number;
  onPage: (page: number) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Satır eylemleri: görüntüle her zaman; düzenle ve diğerleri yetkiye göre. */
  canEdit: boolean;
  scopeKind: 'web' | 'ship';
  onEdit: (item: CertificateListItem) => void;
  rowMenu: (item: CertificateListItem) => RowMenuItem[];
  emptyAction?: ReactNode;
  /**
   * Güncel sertifikanın yerine geçtiği eskiler, yeniden eskiye (`certificateHistory`; ADR 0116
   * §3): satırın altında "History (n)" ile açılır. Sayfalamaya sayılmaz.
   */
  history?: ReadonlyMap<string, readonly CertificateListItem[]> | undefined;
  /** İlk çizimde açık geçmişler (test ve adresten açılan eski sertifika). */
  defaultHistoryOpen?: readonly string[];
}

export interface RowMenuItem {
  key: string;
  label: string;
  icon: 'document' | 'download' | 'remark' | 'endorse' | 'reminder' | 'remove';
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
}

const MENU_ICONS = {
  document: IconEye,
  download: IconDownload,
  remark: IconMessagePlus,
  endorse: IconRosetteDiscountCheck,
  reminder: IconBell,
  remove: IconTrash,
} as const;

const COLUMNS: { key: CertificateSortKey | 'actions'; numeric?: boolean }[] = [
  { key: 'no' },
  { key: 'name' },
  { key: 'type' },
  { key: 'issuedBy' },
  { key: 'issueDate' },
  { key: 'expiryDate' },
  { key: 'status' },
  { key: 'daysLeft', numeric: true },
  { key: 'actions' },
];

/**
 * "Certificate List – SOLAS / Safety Equipment" kartı (`gemi-sertifika-takibi.jpeg`): Certificate
 * Type, Status ve Due Date süzgeçleri, arama; dört özet (koddan); sıralanır ve 15'erli sayfalanır.
 * Toplu işlem olmadığından referanstaki seçim kutusu sütunu yoktur (ADR 0029 Ek: M7d ekranları
 * E5). < 768 px'te tablo kart listesi.
 */
export function CertificateListCard(props: CertificateListCardProps) {
  const {
    scope,
    items,
    scopeCount,
    totalCount,
    summary,
    expiringDays,
    typeOptions,
    typeId,
    status,
    due,
    query,
    onTypeId,
    onStatus,
    onDue,
    onQuery,
    sort,
    onSort,
    page,
    onPage,
    selectedId,
    onSelect,
    canEdit,
    scopeKind,
    onEdit,
    rowMenu,
    emptyAction,
    history,
    defaultHistoryOpen,
  } = props;
  const [historyOpen, setHistoryOpen] = useState<ReadonlySet<string>>(
    () => new Set(defaultHistoryOpen ?? []),
  );
  const toggleHistory = (id: string) =>
    setHistoryOpen((open) => {
      const next = new Set(open);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const { t, i18n } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.list.${key}`, values);
  const number = (value: number) =>
    new Intl.NumberFormat(i18n.resolvedLanguage ?? 'en').format(value);
  const paged = items ? pageOf(items, page, PAGE_SIZE) : null;

  const header = (key: CertificateSortKey | 'actions', numeric?: boolean) => {
    const label = k(`columns.${key}`);
    if (key === 'actions') {
      return (
        <th key={key} scope="col" data-col={key} data-center>
          {label}
        </th>
      );
    }
    const active = sort.key === key;
    const Icon = active
      ? sort.dir === 'asc'
        ? IconSortAscending
        : IconSortDescending
      : IconArrowsSort;
    return (
      <th
        key={key}
        scope="col"
        data-col={key}
        data-numeric={numeric || undefined}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      >
        <UnstyledButton
          className={classes.sortButton}
          data-active={active || undefined}
          onClick={() => onSort(key)}
          aria-label={k('sortBy', { column: label })}
        >
          {label}
          <Icon size={13} className={classes.sortIcon} aria-hidden />
        </UnstyledButton>
      </th>
    );
  };

  const tiles = [
    { key: 'valid', tone: 'success', icon: <IconChecks size={20} />, value: summary?.valid },
    {
      key: 'expiring',
      tone: 'warning',
      icon: <IconBell size={20} />,
      value: summary?.expiring,
    },
    {
      key: 'overdue',
      tone: 'danger',
      icon: <IconAlertTriangle size={20} />,
      value: summary?.overdue,
    },
    { key: 'total', tone: 'info', icon: <IconFileStack size={20} />, value: summary?.total },
  ] as const;

  let body: ReactNode;
  if (paged === null) {
    body = (
      <div className={classes.skeletonRows} aria-hidden>
        {Array.from({ length: 8 }, (_, n) => n).map((n) => (
          <Skeleton key={n} height={18} />
        ))}
      </div>
    );
  } else if (paged.total === 0) {
    body =
      totalCount === 0 ? (
        <EmptyState
          icon={<IconCertificate size={22} />}
          title={k('empty')}
          description={scopeKind === 'web' ? k('emptyOffice') : k('emptyShip')}
          action={emptyAction}
        />
      ) : scopeCount === 0 ? (
        <EmptyState title={k('emptyScope')} />
      ) : (
        <EmptyState title={k('noMatch')} />
      );
  } else {
    const offset = (paged.page - 1) * PAGE_SIZE;
    body = (
      <section
        className={classes.tableBox}
        data-cards
        aria-label={k('table')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={classes.table} data-cards data-certificate-table>
          <thead>
            <tr>{COLUMNS.map((c) => header(c.key, c.numeric))}</tr>
          </thead>
          <tbody>
            {paged.rows.map((item, index) => {
              const tone = daysTone(item);
              const selected = item.id === selectedId;
              const menu = rowMenu(item);
              const older = history?.get(item.id) ?? [];
              const open = older.length > 0 && historyOpen.has(item.id);
              return (
                <Fragment key={item.id}>
                  <tr
                    data-selectable
                    data-selected={selected || undefined}
                    aria-selected={selected}
                    onClick={(event) => {
                      if ((event.target as HTMLElement).closest('button, a, [role="menu"]')) return;
                      onSelect(item.id);
                    }}
                  >
                    <td data-col="no" data-label={k('columns.no')}>
                      {offset + index + 1}
                    </td>
                    <td data-col="name" data-label={k('columns.name')} className={classes.nameCell}>
                      <span className={classes.nameStack}>
                        <UnstyledButton
                          className={classes.nameButton}
                          title={item.name}
                          onClick={() => onSelect(item.id)}
                        >
                          {item.name}
                        </UnstyledButton>
                        {item.officeEntryPending ? (
                          <span className={classes.nameNote}>{k('pendingOffice')}</span>
                        ) : null}
                        {older.length > 0 ? (
                          <UnstyledButton
                            className={classes.historyToggle}
                            aria-expanded={open}
                            aria-label={k('historyToggle', {
                              name: item.name,
                              count: older.length,
                            })}
                            onClick={() => toggleHistory(item.id)}
                          >
                            {open ? (
                              <IconChevronDown size={12} aria-hidden />
                            ) : (
                              <IconChevronRight size={12} aria-hidden />
                            )}
                            {k('history', { count: older.length })}
                          </UnstyledButton>
                        ) : null}
                      </span>
                    </td>
                    <td data-col="type" data-label={k('columns.type')}>
                      <CategoryBadge type={item.type} />
                    </td>
                    <td data-col="issuedBy" data-label={k('columns.issuedBy')}>
                      <span className={classes.issuer} title={item.issuedBy}>
                        {item.issuedBy}
                      </span>
                    </td>
                    <td
                      data-col="issueDate"
                      data-label={k('columns.issueDate')}
                      className={classes.nowrap}
                    >
                      {formatDay(item.issueDate)}
                    </td>
                    <td
                      data-col="expiryDate"
                      data-label={k('columns.expiryDate')}
                      className={classes.nowrap}
                    >
                      {item.expiryDate ? formatDay(item.expiryDate) : k('noExpiry')}
                    </td>
                    <td data-col="status" data-label={k('columns.status')}>
                      <CertificateStatus item={item} />
                    </td>
                    <td data-col="daysLeft" data-label={k('columns.daysLeft')} data-numeric>
                      <span
                        className={classes.days}
                        data-tone={tone ?? undefined}
                        title={
                          item.daysLeft === null
                            ? k('noExpiry')
                            : item.daysLeft < 0
                              ? k('daysOverdue', { count: -item.daysLeft })
                              : k('daysLeft', { count: item.daysLeft })
                        }
                      >
                        {item.daysLeft === null ? '—' : number(item.daysLeft)}
                      </span>
                    </td>
                    <td data-col="actions" data-label={k('columns.actions')} data-center>
                      <span className={classes.rowActions}>
                        <Tooltip label={k('view', { name: item.name })} withinPortal>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={k('view', { name: item.name })}
                            onClick={() => onSelect(item.id)}
                          >
                            <IconEye size={17} />
                          </ActionIcon>
                        </Tooltip>
                        {canEdit && item.source === 'office' ? (
                          <Tooltip label={k('edit', { name: item.name })} withinPortal>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label={k('edit', { name: item.name })}
                              onClick={() => onEdit(item)}
                            >
                              <IconPencil size={16} />
                            </ActionIcon>
                          </Tooltip>
                        ) : null}
                        {menu.length > 0 ? (
                          <Menu position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                aria-label={k('more', { name: item.name })}
                              >
                                <IconDots size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              {menu.map((entry) => {
                                const Icon = MENU_ICONS[entry.icon];
                                return (
                                  <Menu.Item
                                    key={entry.key}
                                    leftSection={<Icon size={15} />}
                                    disabled={entry.disabled === true}
                                    {...(entry.danger ? { color: 'red' } : {})}
                                    onClick={entry.onClick}
                                  >
                                    {entry.label}
                                  </Menu.Item>
                                );
                              })}
                            </Menu.Dropdown>
                          </Menu>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                  {open
                    ? older.map((past) => (
                        <tr
                          key={past.id}
                          data-selectable
                          data-history="true"
                          data-selected={past.id === selectedId || undefined}
                          aria-selected={past.id === selectedId}
                          onClick={(event) => {
                            if ((event.target as HTMLElement).closest('button, a')) return;
                            onSelect(past.id);
                          }}
                        >
                          <td data-col="no" data-label={k('columns.no')} />
                          <td
                            data-col="name"
                            data-label={k('columns.name')}
                            className={classes.nameCell}
                          >
                            <span className={classes.historyName}>
                              <UnstyledButton
                                className={classes.nameButton}
                                title={past.name}
                                onClick={() => onSelect(past.id)}
                              >
                                {past.number}
                              </UnstyledButton>
                            </span>
                          </td>
                          <td data-col="type" data-label={k('columns.type')}>
                            <CategoryBadge type={past.type} />
                          </td>
                          <td data-col="issuedBy" data-label={k('columns.issuedBy')}>
                            <span className={classes.issuer} title={past.issuedBy}>
                              {past.issuedBy}
                            </span>
                          </td>
                          <td
                            data-col="issueDate"
                            data-label={k('columns.issueDate')}
                            className={classes.nowrap}
                          >
                            {formatDay(past.issueDate)}
                          </td>
                          <td
                            data-col="expiryDate"
                            data-label={k('columns.expiryDate')}
                            className={classes.nowrap}
                          >
                            {past.expiryDate ? formatDay(past.expiryDate) : k('noExpiry')}
                          </td>
                          <td data-col="status" data-label={k('columns.status')}>
                            <CertificateStatus item={past} />
                          </td>
                          <td data-col="daysLeft" data-label={k('columns.daysLeft')} data-numeric>
                            —
                          </td>
                          <td data-col="actions" data-label={k('columns.actions')} data-center>
                            <span className={classes.rowActions}>
                              <Tooltip label={k('view', { name: past.number })} withinPortal>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  aria-label={k('view', { name: past.number })}
                                  onClick={() => onSelect(past.id)}
                                >
                                  <IconEye size={17} />
                                </ActionIcon>
                              </Tooltip>
                            </span>
                          </td>
                        </tr>
                      ))
                    : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </section>
    );
  }

  return (
    <Panel>
      <h2 className={classes.listTitle}>{k('title', { scope: scope ?? k('allScope') })}</h2>
      <fieldset className={`${classes.fieldset} ${classes.filters}`}>
        <legend className={classes.srOnly}>{k('filters')}</legend>
        <Select
          label={k('type')}
          size="xs"
          data={[{ value: '', label: k('all') }, ...typeOptions]}
          value={typeId ?? ''}
          allowDeselect={false}
          onChange={(value) => onTypeId(value ? value : null)}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          label={k('statusFilter')}
          size="xs"
          data={STATUS_FILTERS.map((value) => ({ value, label: k(`statuses.${value}`) }))}
          value={status}
          allowDeselect={false}
          onChange={(value) => onStatus((value ?? 'all') as StatusFilter)}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          label={k('due')}
          size="xs"
          data={DUE_FILTERS.map((value) => ({ value, label: k(`dueOptions.${value}`) }))}
          value={due}
          allowDeselect={false}
          onChange={(value) => onDue((value ?? 'all') as DueFilter)}
          comboboxProps={{ withinPortal: true }}
        />
        <TextInput
          size="xs"
          aria-label={k('searchLabel')}
          placeholder={k('search')}
          leftSection={<IconSearch size={14} aria-hidden />}
          value={query}
          onChange={(event) => onQuery(event.currentTarget.value)}
        />
      </fieldset>
      <ul className={classes.tiles} aria-label={k('summary.label')}>
        {tiles.map((tile) => (
          <li key={tile.key} className={classes.tile} data-tone={tile.tone}>
            <span className={classes.tileIcon} aria-hidden>
              {tile.icon}
            </span>
            <span className={classes.tileText}>
              <span className={classes.tileLabel}>
                {k(`summary.${tile.key}`, { days: expiringDays })}
              </span>
              <span className={classes.tileValue}>
                {tile.value === undefined ? '—' : number(tile.value)}
              </span>
            </span>
          </li>
        ))}
      </ul>
      {body}
      {paged && paged.total > 0 ? (
        <div className={classes.pager}>
          <span aria-live="polite">
            {k('showing', { from: paged.from, to: paged.to, total: paged.total })}
          </span>
          <Pagination
            size="sm"
            total={paged.pages}
            value={paged.page}
            onChange={onPage}
            aria-label={k('pagination')}
            getControlProps={(control) => ({
              'aria-label': k(`pager.${control}`),
            })}
          />
        </div>
      ) : null}
    </Panel>
  );
}
